// ============================================================
// Chess Arena — Chest Manager (GDD Section 6.6)
// ============================================================

import { ChestTier, ChestSlot, ChestSlotState } from '../core/types';
import { playerState } from '../core/playerState';
import gameConfig from '../core/gameConfig';
import eventBus from '../core/eventBus';

// --- Default chest config (used before GameConfig loads ChestConfig.json) ---

interface TierConfig {
  unlockTimeMinutes: number;
  dropWeight: number;
}

const DEFAULT_TIER_CONFIGS: Record<ChestTier, TierConfig> = {
  bronze: { unlockTimeMinutes: 60,  dropWeight: 60 },
  silver: { unlockTimeMinutes: 240, dropWeight: 30 },
  gold:   { unlockTimeMinutes: 480, dropWeight: 10 },
  royal:  { unlockTimeMinutes: 720, dropWeight: 0  }, // not in normal rotation
};

const PITY_BRONZE_STREAK_GUARANTEE_SILVER = 10;
const PITY_CHESTS_SINCE_GOLD_GUARANTEE = 20;
const SPEED_UP_COST_PER_30_MIN = 10;

export interface ChestReward {
  coins: number;
  shards?: Record<string, number>;
  charges?: Record<string, number>;
}

class ChestManager {
  /**
   * Earn a chest after a victory. Rolls tier with weighted random + pity.
   * Places in the first empty slot.
   * Returns the slot index and tier, or null if all slots are full.
   */
  earnChest(isFirstChest: boolean = false): { slotIndex: number; tier: ChestTier } | null {
    const slots = [...playerState.chestSlots];
    const emptyIndex = slots.findIndex(s => s.state === 'empty');

    if (emptyIndex === -1) return null;

    let tier: ChestTier;

    if (isFirstChest) {
      // Onboarding: first chest is bronze, locked — tutorial guides the unlock
      tier = 'bronze';
      slots[emptyIndex] = {
        state: 'locked',
        tier,
        unlockEndTime: null,
      };
    } else {
      tier = this.rollTier();
      slots[emptyIndex] = {
        state: 'locked',
        tier,
        unlockEndTime: null,
      };
    }

    playerState.setChestSlots(slots);

    // Update pity counters
    if (tier === 'bronze') {
      playerState.setBronzeStreak(playerState.bronzeStreak + 1);
    } else {
      playerState.setBronzeStreak(0);
    }

    if (tier === 'gold') {
      playerState.setChestsSinceGold(0);
    } else {
      playerState.setChestsSinceGold(playerState.chestsSinceGold + 1);
    }

    return { slotIndex: emptyIndex, tier };
  }

  /**
   * Award a Royal Chest (arena unlock / milestone).
   * Places in first empty slot, or overflow slot if full.
   */
  earnRoyalChest(): { slotIndex: number | 'overflow' } {
    const slots = [...playerState.chestSlots];
    const emptyIndex = slots.findIndex(s => s.state === 'empty');

    const chest: ChestSlot = {
      state: 'locked',
      tier: 'royal',
      unlockEndTime: null,
    };

    if (emptyIndex !== -1) {
      slots[emptyIndex] = chest;
      playerState.setChestSlots(slots);
      return { slotIndex: emptyIndex };
    }

    // Overflow slot — milestone rewards are never lost
    playerState.setOverflowChest(chest);
    return { slotIndex: 'overflow' };
  }

  /**
   * Start the unlock timer on a locked chest.
   * Only 1 chest can be unlocking at a time.
   * Returns false if another is already unlocking or the slot is not locked.
   */
  startUnlocking(slotIndex: number): boolean {
    const slots = [...playerState.chestSlots];

    if (slotIndex < 0 || slotIndex >= slots.length) return false;
    if (slots[slotIndex].state !== 'locked') return false;

    // Check if another chest is already unlocking
    const alreadyUnlocking = slots.some(s => s.state === 'unlocking');
    if (alreadyUnlocking) return false;

    const tier = slots[slotIndex].tier!;
    const config = DEFAULT_TIER_CONFIGS[tier];
    const unlockEndTime = Date.now() + config.unlockTimeMinutes * 60 * 1000;

    slots[slotIndex] = {
      state: 'unlocking',
      tier,
      unlockEndTime,
    };

    playerState.setChestSlots(slots);
    return true;
  }

  /**
   * Update chest states. Mark 'unlocking' chests as 'ready' when timer expires.
   */
  checkTimers(): void {
    const slots = [...playerState.chestSlots];
    let changed = false;
    const now = Date.now();

    for (let i = 0; i < slots.length; i++) {
      if (slots[i].state === 'unlocking' && slots[i].unlockEndTime !== null) {
        if (now >= slots[i].unlockEndTime!) {
          slots[i] = {
            state: 'ready',
            tier: slots[i].tier,
            unlockEndTime: null,
          };
          changed = true;
        }
      }
    }

    if (changed) {
      playerState.setChestSlots(slots);
    }
  }

  /**
   * Open a ready chest. Generate and apply rewards based on tier + current arena.
   * Returns the rewards, or null if the chest is not ready.
   */
  openChest(slotIndex: number): ChestReward | null {
    const slots = [...playerState.chestSlots];

    // Check overflow slot
    if (slotIndex === -1 && playerState.overflowChest?.state === 'ready') {
      const reward = this.generateReward(playerState.overflowChest.tier!);
      playerState.setOverflowChest(null);
      this.applyReward(reward);
      return reward;
    }

    if (slotIndex < 0 || slotIndex >= slots.length) return null;
    if (slots[slotIndex].state !== 'ready') return null;

    const tier = slots[slotIndex].tier!;
    const reward = this.generateReward(tier);

    // Clear the slot
    slots[slotIndex] = {
      state: 'empty',
      tier: null,
      unlockEndTime: null,
    };

    playerState.setChestSlots(slots);
    this.applyReward(reward);
    return reward;
  }

  /**
   * Get all 4 chest slots with current state.
   */
  getSlots(): ChestSlot[] {
    this.checkTimers(); // auto-update before returning
    return playerState.chestSlots;
  }

  /**
   * Skip time on an unlocking chest by the given number of minutes.
   */
  skipTime(slotIndex: number, minutes: number): boolean {
    const slots = [...playerState.chestSlots];
    if (slotIndex < 0 || slotIndex >= slots.length) return false;
    if (slots[slotIndex].state !== 'unlocking') return false;

    const cost = this.getSkipCost(slotIndex, minutes);
    if (playerState.coins < cost) return false;

    playerState.addCoins(-cost);

    slots[slotIndex] = {
      ...slots[slotIndex],
      unlockEndTime: slots[slotIndex].unlockEndTime! - minutes * 60 * 1000,
    };

    playerState.setChestSlots(slots);

    // Check if timer is now expired
    this.checkTimers();
    return true;
  }

  /**
   * Get the coin cost to skip the given number of minutes.
   * 10 coins per 30 minutes (rounded up).
   */
  getSkipCost(slotIndex: number, minutes?: number): number {
    const slots = playerState.chestSlots;
    if (slotIndex < 0 || slotIndex >= slots.length) return 0;
    if (slots[slotIndex].state !== 'unlocking') return 0;

    let minsToSkip: number;
    if (minutes !== undefined) {
      minsToSkip = minutes;
    } else {
      // Cost for remaining time
      const remaining = slots[slotIndex].unlockEndTime! - Date.now();
      minsToSkip = Math.max(0, remaining / (60 * 1000));
    }

    return Math.ceil(minsToSkip / 30) * SPEED_UP_COST_PER_30_MIN;
  }

  /**
   * Get time remaining in ms for an unlocking chest.
   */
  getTimeRemaining(slotIndex: number): number {
    const slots = playerState.chestSlots;
    if (slotIndex < 0 || slotIndex >= slots.length) return 0;
    if (slots[slotIndex].state !== 'unlocking' || slots[slotIndex].unlockEndTime === null) return 0;
    return Math.max(0, slots[slotIndex].unlockEndTime! - Date.now());
  }

  // --- Private ---

  /**
   * Roll a chest tier using weighted random + pity system.
   */
  private rollTier(): ChestTier {
    // Pity: guarantee Silver after consecutive bronzes
    if (playerState.bronzeStreak >= PITY_BRONZE_STREAK_GUARANTEE_SILVER) {
      return 'silver';
    }

    // Pity: guarantee Gold after N chests without gold
    if (playerState.chestsSinceGold >= PITY_CHESTS_SINCE_GOLD_GUARANTEE) {
      return 'gold';
    }

    // Normal weighted roll (excluding royal)
    const tiers: ChestTier[] = ['bronze', 'silver', 'gold'];
    const weights = tiers.map(t => DEFAULT_TIER_CONFIGS[t].dropWeight);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    let roll = Math.random() * totalWeight;

    for (let i = 0; i < tiers.length; i++) {
      roll -= weights[i];
      if (roll <= 0) return tiers[i];
    }

    return 'bronze';
  }

  /**
   * Generate reward contents based on chest tier.
   * Every chest gives exactly 3 slots: coins, 1 shard drop, 1 charge drop.
   * Shard rarity is rolled from weighted probabilities — even low-tier chests
   * have a small chance to drop higher-rarity shards.
   */
  private generateReward(tier: ChestTier): ChestReward {
    if (!gameConfig.isInitialized) {
      return { coins: 20 };
    }

    const chestCfg = gameConfig.getChestConfig();
    const tierCfg = chestCfg?.tiers?.[tier];
    const rewards = tierCfg?.rewards;
    const abilityCfg = gameConfig.getAbilityConfig();

    // Fallback if config not loaded
    if (!rewards || !abilityCfg) {
      return { coins: 20 };
    }

    // Slot 1: Coins
    const coins = this.randomInRange(rewards.coins[0], rewards.coins[1]);

    // Slot 2: Shards — roll one rarity from weighted table, then pick a random ability
    const shards: Record<string, number> = {};
    const rarityWeights = rewards.shardRarityWeights;
    if (rarityWeights) {
      const rolledRarity = this.weightedPick(rarityWeights);
      if (rolledRarity) {
        const candidates = Object.entries(abilityCfg.abilities)
          .filter(([, cfg]) => cfg.rarity === rolledRarity)
          .map(([id]) => id);
        if (candidates.length > 0) {
          const chosen = candidates[Math.floor(Math.random() * candidates.length)];
          const count = this.randomInRange(rewards.shardCount[0], rewards.shardCount[1]);
          if (count > 0) {
            shards[chosen] = count;
          }
        }
      }
    }

    // Slot 3: Charges — pick a random unlocked ability
    const charges: Record<string, number> = {};
    const unlocked = playerState.abilitiesUnlocked;
    if (unlocked.length > 0) {
      const chosen = unlocked[Math.floor(Math.random() * unlocked.length)];
      const count = this.randomInRange(rewards.charges[0], rewards.charges[1]);
      if (count > 0) {
        charges[chosen] = count;
      }
    }

    return {
      coins,
      shards: Object.keys(shards).length > 0 ? shards : undefined,
      charges: Object.keys(charges).length > 0 ? charges : undefined,
    };
  }

  /** Pick a key from a weighted map (e.g. { common: 85, rare: 12, epic: 3 }). */
  private weightedPick(weights: Record<string, number>): string | null {
    const entries = Object.entries(weights);
    const total = entries.reduce((sum, [, w]) => sum + w, 0);
    if (total <= 0) return null;
    let roll = Math.random() * total;
    for (const [key, weight] of entries) {
      roll -= weight;
      if (roll <= 0) return key;
    }
    return entries[entries.length - 1][0];
  }

  private applyReward(reward: ChestReward): void {
    playerState.addCoins(reward.coins);

    // Apply shard rewards
    if (reward.shards) {
      for (const [abilityId, count] of Object.entries(reward.shards)) {
        playerState.addShards(abilityId, count);
        eventBus.emit('shards_earned' as any, { ability: abilityId, count });

        // Check if this unlocks the ability
        if (gameConfig.isInitialized) {
          const abilityCfg = gameConfig.getAbilityConfig();
          const cfg = abilityCfg?.abilities?.[abilityId];
          if (cfg?.shardsToUnlock && !playerState.isAbilityUnlocked(abilityId)) {
            if (playerState.getShards(abilityId) >= cfg.shardsToUnlock) {
              playerState.unlockAbility(abilityId);
              playerState.addCharges(abilityId, cfg.startingCharges, cfg.maxCharges);
              eventBus.emit('ability_unlocked' as any, { ability: abilityId });
            }
          }
        }
      }
    }

    // Apply charge rewards
    if (reward.charges) {
      for (const [abilityId, count] of Object.entries(reward.charges)) {
        if (!playerState.isAbilityUnlocked(abilityId)) continue;
        const maxCharges = gameConfig.isInitialized
          ? gameConfig.getAbilityConfig()?.abilities?.[abilityId]?.maxCharges ?? 20
          : 20;
        playerState.addCharges(abilityId, count, maxCharges);
        eventBus.emit('charges_earned' as any, { ability: abilityId, count });
      }
    }
  }

  private randomInRange(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}

const chestManager = new ChestManager();
export default chestManager;
